import React, { useState, useEffect } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Key, Copy, AlertCircle, CreditCard, History, ArrowLeft } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

interface ApiKey {
  key: string;
  created: string;
  lastUsed: string;
  status: 'active' | 'revoked';
  creditsUsed: number;
}

const ApiDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [copied, setCopied] = useState(false);
  const [totalCredits, setTotalCredits] = useState(0);
  const [usedCredits, setUsedCredits] = useState(0);

  useEffect(() => {
    fetchUserData();
  }, [user]);

  const fetchUserData = async () => {
    if (!user?.uid) return;
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        setTotalCredits(userDoc.data().credits || 0);
        setUsedCredits(userDoc.data().usedCredits || 0);
        // Fetch API keys if they exist
        const keys = userDoc.data().apiKeys || [];
        setApiKeys(keys);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  const generateNewApiKey = async () => {
    if (!user?.uid) return;
    try {
      const newKey = {
        key: `yk_live_${Math.random().toString(36).substr(2, 12)}`,
        created: new Date().toISOString().split('T')[0],
        lastUsed: '-',
        status: 'active' as const,
        creditsUsed: 0
      };

      // Update Firebase
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        apiKeys: arrayUnion(newKey)
      });

      // Update local state
      setApiKeys([...apiKeys, newKey]);
    } catch (error) {
      console.error('Error generating API key:', error);
    }
  };

  const revokeApiKey = async (keyToRevoke: string) => {
    if (!user?.uid) return;
    try {
      // Find the key to revoke
      const keyToUpdate = apiKeys.find(k => k.key === keyToRevoke);
      if (!keyToUpdate) return;

      // Create revoked version of the key
      const revokedKey = {
        ...keyToUpdate,
        status: 'revoked' as const
      };

      // Update Firebase: remove old key and add revoked key
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        apiKeys: arrayRemove(keyToUpdate)
      });
      await updateDoc(userRef, {
        apiKeys: arrayUnion(revokedKey)
      });

      // Update local state
      setApiKeys(apiKeys.map(k => 
        k.key === keyToRevoke ? revokedKey : k
      ));
    } catch (error) {
      console.error('Error revoking API key:', error);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-[#0F1012] min-h-screen text-gray-200">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <button 
              onClick={() => navigate('/chat')}
              className="flex items-center gap-2 text-gray-400 hover:text-gray-200 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to App</span>
            </button>
          </div>
          <h1 className="text-3xl font-bold text-gray-100">API Dashboard</h1>
          <p className="mt-2 text-gray-400">Manage your API keys and monitor usage</p>
        </div>
        <Link
          to="/pricing"
          className="flex items-center gap-2 px-4 py-2 bg-[#00B5A1] text-white rounded-lg hover:bg-[#009688] transition-colors"
        >
          <CreditCard className="w-4 h-4" />
          <span>Buy Credits</span>
        </Link>
      </div>

      {/* Credit Status */}
      <div className="bg-[#1A1B1E] rounded-xl shadow-sm border border-gray-800 p-6 mb-8">
        <h2 className="text-xl font-semibold text-gray-100 mb-4">API Credits</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="p-4 border border-gray-700 rounded-lg bg-[#232429]">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-gray-400">Available Credits</div>
                <div className="mt-1 text-2xl font-semibold text-gray-100">{totalCredits}</div>
              </div>
              <div className="w-12 h-12 bg-[#1d3d39] rounded-full flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-[#00B5A1]" />
              </div>
            </div>
          </div>
          <div className="p-4 border border-gray-700 rounded-lg bg-[#232429]">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-gray-400">Used Credits</div>
                <div className="mt-1 text-2xl font-semibold text-gray-100">{usedCredits}</div>
              </div>
              <div className="w-12 h-12 bg-[#1d3d39] rounded-full flex items-center justify-center">
                <History className="w-6 h-6 text-[#00B5A1]" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create First API Key CTA */}
      {apiKeys.length === 0 && (
        <div className="bg-[#1A1B1E] rounded-xl shadow-sm border border-gray-800 p-8 mb-8 text-center">
          <div className="w-16 h-16 bg-[#1d3d39] rounded-full flex items-center justify-center mx-auto mb-4">
            <Key className="w-8 h-8 text-[#00B5A1]" />
          </div>
          <h3 className="text-xl font-semibold text-gray-100 mb-2">Create Your First API Key</h3>
          <p className="text-gray-400 mb-6 max-w-md mx-auto">
            Get started with the Yesil Health AI API by creating your first API key. You can use this key to make requests to our API endpoints.
          </p>
          <button
            onClick={generateNewApiKey}
            className="bg-[#00B5A1] hover:bg-[#009688] text-white px-6 py-3 rounded-lg transition-colors duration-200"
          >
            Generate API Key
          </button>
        </div>
      )}

      {/* API Keys Management */}
      {apiKeys.length > 0 && (
        <div className="bg-[#1A1B1E] rounded-xl shadow-sm border border-gray-800 p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-100">API Keys</h2>
            <button
              onClick={generateNewApiKey}
              className="bg-[#00B5A1] hover:bg-[#009688] text-white px-4 py-2 rounded-lg transition-colors duration-200"
            >
              Generate New Key
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">API Key</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Created</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Last Used</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Credits Used</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Status</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody>
                {apiKeys.map((key) => (
                  <tr key={key.key} className="border-b border-gray-700">
                    <td className="py-4 px-4">
                      <div className="flex items-center space-x-2">
                        <code className="text-sm text-gray-300">{key.key}</code>
                        <CopyToClipboard 
                          text={key.key}
                          onCopy={() => {
                            setCopied(true);
                            setTimeout(() => setCopied(false), 2000);
                          }}
                        >
                          <button className="text-[#00B5A1] hover:text-[#009688]">
                            <Copy className="w-4 h-4" />
                          </button>
                        </CopyToClipboard>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-sm text-gray-400">{key.created}</td>
                    <td className="py-4 px-4 text-sm text-gray-400">{key.lastUsed}</td>
                    <td className="py-4 px-4 text-sm text-gray-400">{key.creditsUsed}</td>
                    <td className="py-4 px-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        key.status === 'active' ? 'bg-[#1d3d39] text-[#00B5A1]' : 'bg-[#3d1d1d] text-red-400'
                      }`}>
                        {key.status}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-right">
                      {key.status === 'active' && (
                        <button
                          onClick={() => revokeApiKey(key.key)}
                          className="text-red-400 hover:text-red-500 text-sm font-medium"
                        >
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* API Documentation and Usage */}
      <div className="mt-8 bg-[#1A1B1E] rounded-xl shadow-sm border border-gray-800 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-100">API Documentation</h2>
          <a 
            href="https://yesilhealth.com/api/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#00B5A1] hover:text-[#009688] font-medium flex items-center gap-2"
          >
            View Full Documentation
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-100 mb-3">Quick Start</h3>
          <div className="bg-[#121212] rounded-lg p-4 overflow-x-auto">
            <pre className="text-gray-300"><code>{`curl -X POST https://api.yesilhealth.ai/v1/consultation \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "question": "What are the common symptoms of diabetes?",
    "api_model": "yesil-o1-pro",
    "profile_type": "patient"  // or "professional"
  }'`}</code></pre>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-medium text-gray-100 mb-3">Available Models</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-2">
                <div className="w-5 h-5 rounded-full bg-[#1d3d39] flex items-center justify-center flex-shrink-0 mt-1">
                  <div className="w-2 h-2 rounded-full bg-[#00B5A1]"></div>
                </div>
                <div>
                  <span className="font-medium text-gray-200">yesil-o1-mini</span>
                  <p className="text-sm text-gray-400">Standard model for basic medical queries (1 credit per request)</p>
                </div>
              </li>
              <li className="flex items-start gap-2">
                <div className="w-5 h-5 rounded-full bg-[#1d3d39] flex items-center justify-center flex-shrink-0 mt-1">
                  <div className="w-2 h-2 rounded-full bg-[#00B5A1]"></div>
                </div>
                <div>
                  <span className="font-medium text-gray-200">yesil-o1-pro</span>
                  <p className="text-sm text-gray-400">Advanced model with enhanced capabilities (15 credits per request)</p>
                </div>
              </li>
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-medium text-gray-100 mb-3">Profile Types</h3>
            <div className="bg-[#232429] rounded-lg p-4 text-sm border border-gray-700">
              <p className="text-gray-300 mb-2">Choose a user profile to tailor the response format:</p>
              <ul className="space-y-1 text-gray-400">
                <li>• <span className="font-medium text-gray-300">patient</span>: Simplified explanations with accessible language</li>
                <li>• <span className="font-medium text-gray-300">professional</span>: Detailed, technical responses for healthcare providers</li>
              </ul>
              <p className="text-gray-500 mt-2 text-xs">Default: "patient" if not specified</p>
            </div>
          </div>
        </div>

        {/* Response Format Section */}
        <div className="mt-6">
          <h3 className="text-lg font-medium text-gray-100 mb-3">Response Format</h3>
          <div className="bg-[#232429] rounded-lg p-4 text-sm border border-gray-700">
            <p className="text-gray-300 mb-2">The API returns a server-sent events (SSE) stream with real-time updates:</p>
            <ul className="space-y-1 text-gray-400">
              <li>• Processing status updates</li>
              <li>• Specialty determinations</li>
              <li>• Evidence-based medical responses</li>
              <li>• References to medical literature</li>
            </ul>
          </div>
        </div>
      </div>

      {/* API Usage Note */}
      <div className="mt-8 p-4 bg-[#232429] rounded-lg border border-gray-700">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-gray-400 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-gray-300">API Usage Note</h4>
            <p className="mt-1 text-sm text-gray-400">
              Each API request costs 1 credit for the standard model (yesil-o1-mini) and 15 credits for the advanced model (yesil-o1-pro). 
              Make sure to keep your API key secure and never share it publicly.
            </p>
          </div>
        </div>
      </div>

      {/* Toast Notification */}
      {copied && (
        <div className="fixed bottom-4 right-4 bg-[#00B5A1] text-white px-4 py-2 rounded-lg shadow-lg">
          API key copied to clipboard
        </div>
      )}
    </div>
  );
};

export default ApiDashboard; 